import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { getCSRFToken } from '../utils/csrf';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import DroppableGroup from './DroppableGroup';
import UnassignedStudents from './UnassignedStudents';
import RemoveGroup from './RemoveGroup';

import './Groups.css';

const HomeroomGroupsPage = () => {
  const [gradeLevels, setGradeLevels] = useState([]);
  const [selectedGradeLevel, setSelectedGradeLevel] = useState('');
  const [groupData, setGroupData] = useState({
    homeroom: { groups: [], unassignedStudents: [] },
  });
  const [newGroupName, setNewGroupName] = useState('');
  // Keep last valid (truthy) grade level to avoid transient falsy state during DnD
  const lastValidGradeLevelRef = useRef('');

  // Fetch grade levels on mount
  useEffect(() => {
    axios
      .get('/courses/api/grade_levels/')
      .then((res) => setGradeLevels(res.data))
      .catch((err) => console.error('Error fetching grade levels:', err));
  }, []);

  // Fetch homeroom grade-level data
  const fetchGradeLevelData = async (gradeLevel) => {
    console.log(`Homeroom: fetching data for gradeLevel=${gradeLevel}`);
    if (!gradeLevel) {
      console.warn('Homeroom: skip fetch, no grade level selected');
      return;
    }
    try {
      const endpoint = `/api/groups/homeroom-groups/grade-level-data/${gradeLevel}/`;
      const res = await axios.get(endpoint);
      if (res.data?.error) {
        console.error('Homeroom API Error:', res.data.error);
        alert(`API Error: ${res.data.error}`);
        return;
      }
      console.log('Homeroom: fetched data:', res.data);
      setGroupData((prev) => ({
        ...prev,
        homeroom: {
          groups: Array.isArray(res.data.groups) ? res.data.groups : [],
          unassignedStudents: Array.isArray(res.data.unassigned_students)
            ? res.data.unassigned_students
            : [],
        },
      }));
    } catch (err) {
      console.error('Homeroom: error fetching data', err);
      alert('Error fetching homeroom data.');
    }
  };

  useEffect(() => {
    if (selectedGradeLevel) {
      lastValidGradeLevelRef.current = selectedGradeLevel;
    }
    fetchGradeLevelData(selectedGradeLevel);
  }, [selectedGradeLevel]);

  const addGroupManually = async () => {
    if (!newGroupName.trim()) {
      alert('Please enter a group name.');
      return;
    }
    if (!selectedGradeLevel) {
      alert('Please select a grade level.');
      return;
    }

    const endpoint = '/api/groups/homeroom-groups/add/';
    const payload = {
      name: newGroupName,
      grade_level: Number(selectedGradeLevel),
    };

    console.log('Homeroom addGroupManually POST', { endpoint, payload });
    try {
      const res = await axios.post(endpoint, payload, {
        headers: { 'X-CSRFToken': getCSRFToken() },
      });
      console.log('Homeroom group added:', res.data);
      setNewGroupName('');
      await fetchGradeLevelData(selectedGradeLevel);
    } catch (error) {
      const detail = error?.response?.data
        ? JSON.stringify(error.response.data)
        : error.message;
      console.error('Error adding homeroom group:', detail);
      alert('Failed to add homeroom group: ' + detail);
    }
  };

  const handleDrop = async (item, targetGroup) => {
    // Snapshot context to avoid race when user switches tab/grade mid-op
    const opGradeLevel = selectedGradeLevel || lastValidGradeLevelRef.current;
    try {
      console.log('Homeroom drag item:', item);
      console.log('Homeroom target group:', targetGroup);

      if (!opGradeLevel) {
        alert('Please select a grade level before moving students.');
        return;
      }

      // Move to unassigned
      if (!targetGroup) {
        const endpoint = '/api/groups/homeroom-groups/update-membership/';
        await axios.post(
          endpoint,
          {
            student_id: item.id,
            group_id: null,
            grade_level: Number(opGradeLevel),
          },
          { headers: { 'X-CSRFToken': getCSRFToken() }, withCredentials: true }
        );
        // Refetch canonical state instead of optimistic updates
        await fetchGradeLevelData(opGradeLevel);
        console.log('Homeroom: moved to unassigned (refetched).');
        return;
      }

      // Move to specific group
      const endpoint = '/api/groups/homeroom-groups/update-membership/';
      await axios.post(
        endpoint,
        {
          student_id: item.id,
          group_id: targetGroup.id,
          grade_level: Number(opGradeLevel),
        },
        { headers: { 'X-CSRFToken': getCSRFToken() }, withCredentials: true }
      );

      // Refetch canonical state instead of optimistic updates
      await fetchGradeLevelData(opGradeLevel);
      console.log('Homeroom: moved between groups (refetched).');
    } catch (error) {
      console.error('Homeroom: handleDrop error:', error?.message || error);
      alert('Error updating homeroom membership: ' + (error?.message || error));
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="groups-home">
        <h2 className="title">Manage Homeroom Groups</h2>

        <div className="top-section">
          <div className="filter-group">
            <label htmlFor="grade-level-select">Grade Level:</label>
            <select
              id="grade-level-select"
              value={selectedGradeLevel}
              onChange={(e) => {
                const val = e.target.value;
                setSelectedGradeLevel(val);
                if (val) {
                  lastValidGradeLevelRef.current = val;
                }
              }}
            >
              <option value="">Select Grade</option>
              {gradeLevels.map((gl) => (
                <option key={gl.id} value={gl.id}>
                  {gl.name || gl.grade}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <button onClick={() => fetchGradeLevelData(selectedGradeLevel)}>Refresh</button>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Enter new group name"
            />
            <button onClick={addGroupManually}>Add Homeroom Group</button>
          </div>
        </div>

        <div className="layout">
          <UnassignedStudents
            students={groupData.homeroom?.unassignedStudents || []}
            onDrop={handleDrop}
          />

          <div className="group-list">
            {console.log('Homeroom Render:', {
              selectedGradeLevel,
              groupsCount: (groupData.homeroom?.groups || []).length,
              unassignedCount: (groupData.homeroom?.unassignedStudents || []).length,
            })}
            {(groupData.homeroom?.groups || []).map((group) => (
              <div key={group.id} className="group-container">
                <div className="group-header">
                  <h3>{group.name}</h3>
                  <RemoveGroup
                    groupId={group.id}
                    groupName={group.name}
                    groupType={'homeroom'}
                    onRemove={(removedGroupId, removedStudents) => {
                      setGroupData((prev) => {
                        const updatedGroups =
                          prev.homeroom?.groups?.filter((g) => g.id !== removedGroupId) || [];

                        const updatedUnassignedStudents = [
                          ...(prev.homeroom?.unassignedStudents || []),
                          ...(removedStudents || [])
                            .filter((student) => student && student.id)
                            .map((student) => ({
                              id: student.id,
                              first_name: student.first_name,
                              last_name: student.last_name,
                            })),
                        ];

                        return {
                          ...prev,
                          homeroom: {
                            groups: updatedGroups,
                            unassignedStudents: updatedUnassignedStudents,
                          },
                        };
                      });
                    }}
                  />
                </div>
                <DroppableGroup key={group.id} group={group} onDrop={handleDrop} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </DndProvider>
  );
};

export default HomeroomGroupsPage;
